import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { isAdmin } from "../shared utils/auth";
import { getStudentStays } from "../Agents/api/agentsApi";
import { getStudentsByIds } from "./api/people";
import { settleWithConcurrency } from "./utils/concurrency";
import { fmtDate } from "./utils/dates";
import { resolveGroupStay, summariseAgents } from "./utils/groupAgent";
import GroupAgentDialog from "./GroupAgentDialog";

const studentName = (s) =>
  `${s.studentName || s.name || ""} ${s.studentSurname || s.surname || ""}`.trim() || `#${s.id}`;

// "Agent" block on the booking-group Details tab. The link lives on each student's
// stay, so this reads every member's stays (the per-student feed is the one that
// carries the agent — the batch feed doesn't) and rolls them up. Everyone sees who
// the agent is; only ADMIN gets the button, matching the student Stays tab.
export default function GroupAgentSection({ groupName, studentIds = [], meta, token }) {
  const [raw, setRaw] = useState([]); // [{ studentId, name, stays: [] | null }]
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const campus = meta?.defaultCampus || "";
  const start = meta?.defaultStartDate ? fmtDate(meta.defaultStartDate) : "";
  const end = meta?.defaultEndDate ? fmtDate(meta.defaultEndDate) : "";

  // Fetch once per membership; which stay counts as "the group stay" is resolved
  // below so editing the group dates doesn't refetch every student.
  const load = useCallback(async () => {
    if (!studentIds.length) {
      setRaw([]);
      return;
    }
    setLoading(true);
    try {
      const [students, settled] = await Promise.all([
        getStudentsByIds(studentIds, token).catch(() => []),
        settleWithConcurrency(studentIds, (id) => getStudentStays(id), 4),
      ]);
      const nameById = {};
      (Array.isArray(students) ? students : []).forEach((s) => {
        nameById[s.id] = studentName(s);
      });
      setFailed(settled.filter((r) => r.status === "rejected").length);
      setRaw(
        settled.map((r) => ({
          studentId: r.item,
          name: nameById[r.item] || `#${r.item}`,
          stays: r.status === "fulfilled" && Array.isArray(r.value) ? r.value : null,
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [studentIds, token]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () =>
      raw.map((r) => ({
        studentId: r.studentId,
        name: r.name,
        stay: r.stays ? resolveGroupStay(r.stays, { campus, start, end }) : null,
      })),
    [raw, campus, start, end]
  );

  const summary = useMemo(() => summariseAgents(rows), [rows]);
  const resolvable = useMemo(() => rows.filter((r) => r.stay), [rows]);
  const admin = isAdmin();

  const headline = () => {
    if (!studentIds.length) return <em>No students yet — the agent is linked per student stay.</em>;
    if (loading && !rows.length) return null;
    if (!summary.agents.length) return <em>No agent linked.</em>;
    return (
      <Stack spacing={0.5}>
        {summary.agents.map((a) => (
          <Stack key={a.agentId} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body1" fontWeight={600}>
              {a.agentName}
            </Typography>
            {a.terms.map((t) => (
              <Chip key={t} size="small" label={t} variant="outlined" />
            ))}
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              {a.count} of {resolvable.length} stay{resolvable.length === 1 ? "" : "s"}
            </Typography>
          </Stack>
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Agent
          </Typography>
          {headline()}
          {!loading && rows.length > 0 ? (
            <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mt: 0.5 }}>
              {summary.unlinked > 0 ? `${summary.unlinked} unlinked. ` : ""}
              {summary.noStay > 0
                ? `${summary.noStay} with no stay matching the group window (fix on the Stays tab). `
                : ""}
              {failed > 0 ? `${failed} couldn't be read. ` : ""}
              Read from each student&apos;s group stay.
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {loading ? <CircularProgress size={18} /> : null}
          {admin ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              disabled={loading || !resolvable.length}
            >
              {summary.agents.length ? "Change agent…" : "Link agent…"}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <GroupAgentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        rows={resolvable}
        summary={summary}
        groupName={groupName}
        onSaved={load}
      />
    </Box>
  );
}
